import React, { useState, useMemo, useCallback, memo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import siteData from "../siteData";

const works = siteData.works;

const fadeUp = {
  hidden: { opacity: 0, y: 40, filter: "blur(8px)" },
  visible: {
    opacity: 1,
    y: 0,
    filter: "blur(0px)",
    transition: { duration: 0.8, ease: [0.19, 1, 0.22, 1] },
  },
};

const stagger = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.15, delayChildren: 0.1 },
  },
};

const showcaseAnim = {
  hidden: { opacity: 0, y: 60 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.9, ease: [0.19, 1, 0.22, 1] },
  },
};

const vp = { once: true, amount: 0.15 };

const ShowcaseCard = memo(({ item, index, onClick }) => (
  <motion.div
    className="showcase-card"
    variants={showcaseAnim}
    initial="hidden"
    whileInView="visible"
    viewport={{ once: true, amount: 0.2 }}
  >
    {/* Image */}
    <div
      className="showcase-image-wrapper cursor-expand"
      onClick={() => onClick(item)}
    >
      <img src={item.imageURL} alt={item.title} loading="lazy" />
      <div className="showcase-image-overlay" />
    </div>

    {/* Info */}
    <div className="showcase-info">
      <span className="showcase-number">
        {String(index + 1).padStart(2, "0")}
      </span>
      <h3 className="showcase-title">{item.title}</h3>
      <p className="showcase-desc">{item.modalObj.description}</p>
      <div className="showcase-tags">
        {item.modalObj.categories.map((cat, i) => (
          <span key={i} className="tag">
            {cat}
          </span>
        ))}
      </div>
      <div className="showcase-links">
        {item.modalObj.links.map((link, i) => (
          <a
            key={i}
            href={link.url}
            target="_blank"
            rel="noopener noreferrer"
            className="showcase-link"
          >
            {link.title === "Github" ? (
              <>
                <i className="fa fa-github" /> GitHub{" "}
                <span className="arrow">&rarr;</span>
              </>
            ) : (
              <>
                <i className="fa fa-external-link" /> {link.title}{" "}
                <span className="arrow">&rarr;</span>
              </>
            )}
          </a>
        ))}
      </div>
    </div>
  </motion.div>
));

const PortfolioModal = memo(({ item, onClose }) => (
  <motion.div
    className="modal-overlay"
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    exit={{ opacity: 0 }}
    onClick={onClose}
  >
    <motion.div
      className="modal-content"
      initial={{ opacity: 0, scale: 0.92, y: 30 }}
      animate={{ opacity: 1, scale: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.92, y: 30 }}
      transition={{ duration: 0.4, ease: [0.19, 1, 0.22, 1] }}
      onClick={(e) => e.stopPropagation()}
    >
      <img
        className="modal-image"
        src={item.modalObj.imageURL}
        alt={item.title}
      />
      <div className="modal-body">
        <h3>{item.title}</h3>
        <p>{item.modalObj.description}</p>
        <div className="modal-tags">
          {item.modalObj.categories.map((cat, i) => (
            <span key={i} className="tag">
              {cat}
            </span>
          ))}
        </div>
        <div className="modal-links">
          {item.modalObj.links.map((link, i) => (
            <a
              key={i}
              href={link.url}
              target="_blank"
              rel="noopener noreferrer"
            >
              {link.title === "Github" ? (
                <>
                  <i className="fa fa-github" /> GitHub
                </>
              ) : (
                <>
                  <i className="fa fa-external-link" /> {link.title}
                </>
              )}
            </a>
          ))}
          <button className="modal-close" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </motion.div>
  </motion.div>
));

export default function Portfolio() {
  const [chosenItem, setChosenItem] = useState(null);

  const sortedWorks = useMemo(
    () => [...works].sort((a, b) => b.importance - a.importance).slice(0, 6),
    [],
  );
  const handleClick = useCallback((item) => setChosenItem(item), []);
  const handleClose = useCallback(() => setChosenItem(null), []);

  return (
    <section className="portfolio section" id="portfolio">
      <motion.div
        className="container"
        initial="hidden"
        whileInView="visible"
        viewport={vp}
        variants={stagger}
      >
        {/* Watermark */}
        <div className="section-watermark">WORKS</div>

        <div className="section-header">
          <motion.div variants={fadeUp}>
            <span className="section-tag">Portfolio</span>
          </motion.div>
          <motion.h2 className="section-title" variants={fadeUp}>
            Featured Projects
          </motion.h2>
          <motion.p className="section-subtitle" variants={fadeUp}>
            A selection of projects I've built and contributed to.
          </motion.p>
        </div>

        {/* Showcase cards */}
        {sortedWorks.map((work, index) => (
          <ShowcaseCard
            key={work.modalNum}
            item={work}
            index={index}
            onClick={handleClick}
          />
        ))}
      </motion.div>

      <AnimatePresence>
        {chosenItem && (
          <PortfolioModal item={chosenItem} onClose={handleClose} />
        )}
      </AnimatePresence>
    </section>
  );
}
